import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomQuestionnaireService } from 'src/app/pages/questionarios/services/custom-questionnaire.service';
import {
  ICustomQuestionnaire,
  ICustomQuestionnaireQuestion,
  IUpsertCustomQuestionnairePayload
} from 'src/app/pages/setups/models/questionnaire.interface';
import { ToastService } from 'src/app/shared/services/toast.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { CompanyService } from 'src/app/pages/companies/services/company.service';
import { ICompany } from 'src/app/pages/companies/models/company.interface';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { canPickCompany } from 'src/app/shared/utilities/company-scope';

@Component({
  selector: 'app-add-new-questionario',
  templateUrl: './add-new-questionario.component.html',
  styleUrls: ['./add-new-questionario.component.scss']
})
export class AddNewQuestionarioComponent implements OnInit {
  form: FormGroup;
  questionnaires: ICustomQuestionnaire[] = [];
  selectedCompanyId?: string;
  companies: ICompany[] = [];
  isLoading = false;
  isSaving = false;
  isCompanyLoading = false;
  // Interno e parceiro sem empresa escolhem a empresa; usuário de empresa fica fixo na própria.
  podeEscolherEmpresa = false;
  ERoleAdminString = ERoleAdminString;
  editingQuestionnaireId: string | null = null;
  companySelector = new FormControl('');

  constructor(
    private readonly fb: FormBuilder,
    private readonly customQuestionnaireService: CustomQuestionnaireService,
    private readonly toastService: ToastService,
    private readonly sessionService: SessionService,
    private readonly companyService: CompanyService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      category: ['', [Validators.required]],
      questions: this.fb.array([])
    });
  }

  ngOnInit(): void {
    const session = this.sessionService.getSession();
    this.podeEscolherEmpresa = canPickCompany(session);
    this.selectedCompanyId = session?.companyId;

    if (this.selectedCompanyId) {
      this.loadQuestionnaires();
    }

    if (this.podeEscolherEmpresa) {
      this.companySelector.setValue(this.selectedCompanyId ?? '', { emitEvent: false });
      this.loadCompanies();
    }

    if (!this.questions.length) {
      this.addQuestion();
    }

    this.companySelector.valueChanges.subscribe((companyId) => {
      if (this.podeEscolherEmpresa) {
        this.onCompanySelected(companyId as string);
      }
    });
  }

  get questions(): FormArray {
    return this.form.get('questions') as FormArray;
  }

  private setQuestions(questions: ICustomQuestionnaireQuestion[]): void {
    const questionsArray = this.fb.array((questions ?? []).map((question) => this.createQuestionGroup(question)));

    if (!questionsArray.length) {
      questionsArray.push(this.createQuestionGroup());
    }

    this.form.setControl('questions', questionsArray);
  }

  questionAnswers(questionIndex: number): FormArray {
    return this.questions.at(questionIndex).get('answers') as FormArray;
  }

  addQuestion(): void {
    this.questions.push(this.createQuestionGroup());
  }

  removeQuestion(index: number): void {
    if (this.questions.length === 1) {
      const firstQuestion = this.questions.at(0) as FormGroup;
      firstQuestion.get('id')?.reset(null);
      firstQuestion.get('question')?.reset('');
      const answers = this.questionAnswers(0);
      while (answers.length) {
        answers.removeAt(0);
      }
      answers.push(this.createAnswerGroup());
      answers.push(this.createAnswerGroup());
      return;
    }

    this.questions.removeAt(index);
  }

  addAnswer(questionIndex: number): void {
    this.questionAnswers(questionIndex).push(this.createAnswerGroup());
  }

  removeAnswer(questionIndex: number, answerIndex: number): void {
    const answers = this.questionAnswers(questionIndex);

    if (answers.length === 1) {
      answers.at(0).reset({ id: null, answer: '' });
      return;
    }

    answers.removeAt(answerIndex);
  }

  saveQuestionnaire(): void {
    if (!this.selectedCompanyId) {
      this.toastService.toast('Selecione uma empresa para vincular o questionário.');
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.buildPayload(this.selectedCompanyId);

    this.isSaving = true;
    const request$ = this.editingQuestionnaireId
      ? this.customQuestionnaireService.update(this.editingQuestionnaireId, payload)
      : this.customQuestionnaireService.create(payload);

    request$.subscribe({
      next: () => {
        this.toastService.toast(`Questionário ${this.editingQuestionnaireId ? 'atualizado' : 'criado'} com sucesso!`);
        this.resetForm();
        this.loadQuestionnaires();
      },
      error: (error) => {
        console.error('Erro ao salvar questionário personalizado', error);
        this.toastService.toast('Não foi possível salvar o questionário. Tente novamente mais tarde.');
        this.isSaving = false;
      }
    });
  }

  startEditing(questionnaire: ICustomQuestionnaire): void {
    this.editingQuestionnaireId = questionnaire.id;
    this.patchForm(questionnaire);
  }

  cancelEditing(): void {
    this.resetForm();
  }

  deleteQuestionnaire(questionnaireId: string): void {
    if (!confirm('Tem certeza que deseja excluir este questionário?')) {
      return;
    }

    this.customQuestionnaireService.delete(questionnaireId).subscribe({
      next: () => {
        this.toastService.toast('Questionário removido com sucesso!');
        this.loadQuestionnaires();
        if (this.editingQuestionnaireId === questionnaireId) {
          this.resetForm();
        }
      },
      error: (error) => {
        console.error('Erro ao remover questionário', error);
        this.toastService.toast('Não foi possível remover o questionário.');
      }
    });
  }

  trackByQuestionnaireId(_: number, item: ICustomQuestionnaire): string {
    return item.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  isInvalid(controlPath: string): boolean {
    const control = this.form.get(controlPath);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  loadQuestionnaires(): void {
    if (!this.selectedCompanyId) {
      return;
    }

    this.isLoading = true;
    this.customQuestionnaireService.getByCompanyId(this.selectedCompanyId).subscribe({
      next: (questionnaires) => {
        this.questionnaires = questionnaires ?? [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar questionários personalizados', error);
        this.toastService.toast('Não foi possível carregar os questionários personalizados.');
        this.isLoading = false;
      }
    });
  }

  private loadCompanies(): void {
    this.isCompanyLoading = true;
    this.companyService.listarEmpresasAtivas().subscribe({
      next: (response) => {
        this.companies = response.data ?? [];
        this.isCompanyLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar empresas', error);
        this.toastService.toast('Não foi possível carregar as empresas.');
        this.isCompanyLoading = false;
      }
    });
  }

  onCompanySelected(companyId: string | null): void {
    this.selectedCompanyId = companyId ?? undefined;
    this.questionnaires = [];
    this.resetForm();

    if (this.selectedCompanyId) {
      this.loadQuestionnaires();
    }
  }

  private createAnswerGroup(answer?: { id?: string; answer?: string }): FormGroup {
    return this.fb.group({
      id: [answer?.id ?? null],
      answer: [answer?.answer ?? '', [Validators.required]]
    });
  }

  private createQuestionGroup(question?: ICustomQuestionnaireQuestion): FormGroup {
    const answers = this.fb.array(
      question?.answers?.length
        ? question.answers.map((answer) => this.createAnswerGroup(answer))
        : [this.createAnswerGroup(), this.createAnswerGroup()]
    );

    return this.fb.group({
      id: [question?.id ?? null],
      question: [question?.question ?? '', [Validators.required]],
      answers
    });
  }

  private buildPayload(companyId: string): IUpsertCustomQuestionnairePayload {
    const { name, category, questions } = this.form.getRawValue();

    return {
      companyId,
      name,
      category,
      questions: questions.map((question: ICustomQuestionnaireQuestion) => ({
        id: question.id,
        question: question.question,
        answers: (question.answers || []).map((answer) => ({
          id: answer.id,
          answer: answer.answer
        }))
      }))
    };
  }

  private patchForm(questionnaire: ICustomQuestionnaire): void {
    this.form.patchValue({
      name: questionnaire.name,
      category: questionnaire.category
    });

    this.setQuestions(questionnaire.questions ?? []);
  }

  private resetForm(): void {
    this.form.reset({
      name: '',
      category: ''
    });
    this.setQuestions([]);
    this.isSaving = false;
    this.editingQuestionnaireId = null;
  }
}
